import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscribeToChannel } from './components/molecules'
import {
  FaqView,
  RewardsView,
  SettingsView,
  TableView,
} from './components/pages'
import { MainLayout } from './components/templates'
import {
  useAnal,
  useHandleBackButton,
  useModal,
  useNavigation,
  useStartParam,
  useTableNavigation,
} from './hooks'
import { appContainer } from './index.css'
import { userSettingsAtom } from './store/atoms'
import { EModalName } from './types/enums'
import { trpc } from './utils'

const App = () => {
  const { t } = useTranslation()
  const { currentPage } = useNavigation()
  const { openConfirmationModal } = useModal()
  const { joinTable, changeTable, closeTable } = useTableNavigation()

  const {
    inviterUserId,
    sourceOrigin,
    isLoaded: isStartParamLoaded,
  } = useStartParam({
    joinTable,
  })
  const getUser = trpc.user.getUser.useQuery(
    {
      inviterUserId,
      sourceOrigin,
    },
    {
      enabled: isStartParamLoaded,
    },
  )
  const requestAllRewards = trpc.quest.requestAllRewards.useMutation()
  const { logEvent, setUserId } = useAnal()

  const [userSettings, setUserSettings] = useAtom(userSettingsAtom)

  const isCheckedUnclaimedRewardsAtLaunch = useRef(false)

  const handleUnclaimedRewards = async (rewardableQuests: number) => {
    if (!isCheckedUnclaimedRewardsAtLaunch.current && rewardableQuests > 0) {
      const { closeModal } = openConfirmationModal(
        {
          text: t('modal.confirmation.unclaimedRewards.text', {
            rewardsCount: rewardableQuests,
          }),
          mainButtonText: t('modal.confirmation.unclaimedRewards.confirm'),
          secondaryButtonText: t('modal.confirmation.unclaimedRewards.cancel'),
        },
        {
          title: t('modal.confirmation.unclaimedRewards.title'),
          name: EModalName.UNCLAIMED_REWARDS,
        },
      )
      isCheckedUnclaimedRewardsAtLaunch.current = true
      const result = await closeModal
      if (result === 'main') {
        await requestAllRewards.mutateAsync()
        await getUser.refetch()
      }
    }
  }

  useEffect(() => {
    if (getUser.data) {
      setUserId(getUser.data.id)
      void handleUnclaimedRewards(getUser.data.rewardableQuests)
      if (
        getUser.data.langCode &&
        userSettings.langCode !== getUser.data.langCode
      ) {
        setUserSettings((old) => ({
          ...old,
          langCode: getUser.data.langCode,
        }))
      }
    }
  }, [getUser.data])

  useEffect(() => {
    logEvent('screen_view', {
      firebase_screen: currentPage.page,
    })
    if (
      currentPage.page === 'main' ||
      currentPage.page === 'connectToPrivateTable' ||
      currentPage.page === 'createPrivateTable'
    ) {
      void getUser.refetch()
    }
  }, [currentPage.page])

  useHandleBackButton()

  return (
    <div className={appContainer}>
      {getUser.data ? (
        currentPage.page === 'table' ? (
          <TableView
            userId={getUser.data.id}
            currentTableId={currentPage.options.tableId}
            currentPassword={currentPage.options.password}
            tables={currentPage.options.multitable}
            onChangeTable={changeTable}
            onJoinNewTable={joinTable}
            onCloseTable={closeTable}
          />
        ) : currentPage.page === 'rewards' ? (
          <RewardsView />
        ) : currentPage.page === 'settings' ? (
          <SettingsView />
        ) : currentPage.page === 'faq' ? (
          <FaqView />
        ) : (
          <MainLayout
            type='mainPage'
            profileData={getUser.data}
            onJoinNewTable={joinTable}
          />
        )
      ) : (
        <SubscribeToChannel />
      )}
    </div>
  )
}

export default App
